import { useLanguage } from '@/contexts/LanguageContext';
import { Button } from '@/components/ui/button';
import { Shield, ArrowRight, Sparkles, ChevronDown } from 'lucide-react';
import logo from '@/assets/logo-ghost-small.webp';

const HeroSection = () => {
  const { t, language } = useLanguage();

  const scrollToProducts = () => {
    document.getElementById('products')?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <section className="relative min-h-screen flex items-center justify-center overflow-hidden">
      {/* Content */}
      <div className="relative z-20 container mx-auto px-4 pt-24 pb-16">
        <div className="flex flex-col items-center text-center max-w-4xl mx-auto">
          {/* Badge */}
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-primary/20 bg-primary/5 mb-8 animate-fade-in-up backdrop-blur-sm">
            <Shield className="w-4 h-4 text-primary" />
            <span className="text-sm font-medium text-primary">{t('hero.badge')}</span>
          </div>

          {/* Ghost Logo Animation */}
          <div className="relative mb-10 animate-fade-in" style={{ animationDelay: '0.2s' }}>
            <img 
              src={logo} 
              alt="Ziqo Ghost" 
              className="w-32 h-32 md:w-44 md:h-44 animate-float drop-shadow-[0_0_30px_hsl(var(--primary)/0.5)]"
              width={176}
              height={176}
              decoding="async"
              fetchPriority="high"
            />
            <div className="absolute inset-0 w-32 h-32 md:w-44 md:h-44 bg-primary/20 rounded-full blur-3xl animate-pulse" />
          </div>

          {/* Title */}
          <h1 className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-black mb-6 font-['Orbitron'] tracking-wide animate-fade-in-up" style={{ animationDelay: '0.3s' }}>
            <span className="text-gradient glow-text">{t('hero.title')}</span>
          </h1>

          {/* Subtitle */}
          <p className="text-lg md:text-xl font-medium text-foreground/90 mb-4 font-['Orbitron'] tracking-wide animate-fade-in-up" style={{ animationDelay: '0.4s' }}>
            {t('hero.subtitle')}
          </p>

          {/* Description */}
          <p className="text-base md:text-lg text-muted-foreground max-w-2xl mb-10 leading-relaxed animate-fade-in-up" style={{ animationDelay: '0.5s' }}>
            {t('hero.description')}
          </p>

          {/* CTA Buttons */}
          <div className="flex flex-col sm:flex-row gap-4 animate-fade-in-up" style={{ animationDelay: '0.6s' }}>
            <Button 
              size="lg" 
              className="bg-primary hover:bg-primary/90 text-primary-foreground font-semibold px-8 py-6 text-base glow group transition-all duration-300"
              onClick={scrollToProducts}
            >
              <Sparkles className="w-5 h-5 mr-2" />
              {t('hero.cta')}
              <ArrowRight className="w-5 h-5 ml-2 group-hover:translate-x-1 transition-transform" />
            </Button>
            
            <Button 
              size="lg" 
              variant="outline"
              className="border-primary/30 text-foreground hover:bg-primary/5 hover:border-primary/50 px-8 py-6 text-base transition-all duration-300"
              onClick={() => document.getElementById('pricing')?.scrollIntoView({ behavior: 'smooth' })}
            >
              {t('hero.secondary')}
            </Button>
          </div>

          {/* Stats */}
          <div className="grid grid-cols-3 gap-6 md:gap-12 mt-20 pt-10 border-t border-border/30 animate-fade-in-up" style={{ animationDelay: '0.7s' }}>
            <div className="text-center group">
              <div className="text-2xl md:text-4xl font-bold text-gradient font-['Orbitron'] group-hover:scale-105 transition-transform">100%</div>
              <div className="text-xs md:text-sm text-muted-foreground mt-2">
                غیرقابل شناسایی
              </div>
            </div>
            <div className="text-center group">
              <div className="text-2xl md:text-4xl font-bold text-gradient font-['Orbitron'] group-hover:scale-105 transition-transform">24/7</div>
              <div className="text-xs md:text-sm text-muted-foreground mt-2">
                پشتیبانی
              </div>
            </div>
            <div className="text-center group">
              <div className="text-2xl md:text-4xl font-bold text-gradient font-['Orbitron'] group-hover:scale-105 transition-transform">5K+</div>
              <div className="text-xs md:text-sm text-muted-foreground mt-2">
                کاربران
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Scroll indicator */}
      <button 
        onClick={scrollToProducts}
        className="absolute bottom-8 left-1/2 -translate-x-1/2 z-20 text-muted-foreground hover:text-primary transition-colors animate-bounce"
        aria-label="Scroll to products"
      >
        <ChevronDown className="w-8 h-8" />
      </button>
    </section>
  );
};

export default HeroSection;
